import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Table, Th, Td, Title } from './style';
import { Header } from "../Header";
import { apiOperation, apiExtractor, headers, apiPetro } from "../../services/api";
import { Resultado } from "../../models/base/basic";
import { getSessionUser } from "../../utils/users";
import { formatDateToBR } from '../../utils';

type SumulaResumo = {
    codigoGrupo: string;
    nomeGrupo: string;
    codigoCliente?: string;
    statusSumula: number;
    id: string;
    dataCriacao: string;
};

type SumulaResponse = {
    numeroPagina: number;
    tamanhoPagina: number;
    totalItens: number;
    totalPaginas: number;
    itens: SumulaResumo[];
};

export const SumulaList: React.FC = () => {
    const [sumulas, setSumulas] = useState<SumulaResumo[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const navigate = useNavigate();
    const [pagina, setPagina] = useState(1);
    const [tamanho, setTamanho] = useState(10); // padrão: 10 itens por página
    const [totalPaginas, setTotalPaginas] = useState(1);
    const [codigoGrupoFiltro, setCodigoGrupoFiltro] = useState('');
    const [codigoGrupoBusca, setCodigoGrupoBusca] = useState('');
    const [statusFiltro, setStatusFiltro] = useState('');
    const [statusBusca, setStatusBusca] = useState('');
    const [usuarioCriacaoFiltro, setUsuarioCriacaoFiltro] = useState('');
    const [usuarioCriacaoBusca, setUsuarioCriacaoBusca] = useState('');
    const [nomeGrupoFiltro, setNomeGrupoFiltro] = useState('');
    const [nomeGrupoBusca, setNomeGrupoBusca] = useState('');
    const [codigoClienteFiltro, setCodigoClienteFiltro] = useState('');
    const [codigoClienteBusca, setCodigoClienteBusca] = useState('');
    const [dataInicialFiltro, setDataInicialFiltro] = useState('');
    const [dataInicialBusca, setDataInicialBusca] = useState('');
    const [dataFinalFiltro, setDataFinalFiltro] = useState('');
    const [dataFinalBusca, setDataFinalBusca] = useState('');
    const [erroPeriodo, setErroPeriodo] = useState('');
    const [usuarioLogado, setUsuarioLogado] = useState<any>(null);

    // Função para mapear o status ID para o texto correspondente
    const getStatusText = (statusId: string | number): string => {
        const statusMap: { [key: string]: string } = {
            '0': 'Assessor',
            '1': 'Gestão Riscos',
            '2': 'Gerente Negócios',
            '3': 'Coordenação Riscos',
            '4': 'Diretoria',
            '5': 'Despachado',
            '6': 'Finalizado'
        };

        return statusMap[statusId.toString()] || statusId.toString();
    };

    // Usamos a função utilitária para formatar datas
    // const formatDate = formatDateToBR; // Já importada

    useEffect(() => {
        const user = getSessionUser();
        setUsuarioLogado(user);
    }, []);

    useEffect(() => {
        const fetchSumulas = async () => {
            try {
                setLoading(true);
                const config = {
                    headers: headers(),
                    params: {
                        numeroPagina: pagina,
                        tamanhoPagina: tamanho,
                        codigoGrupo: codigoGrupoFiltro,
                        status: statusFiltro || undefined,
                        usuarioCriacao: usuarioCriacaoFiltro || undefined,
                        nomeGrupo: nomeGrupoFiltro || undefined,
                        codigoCliente: codigoClienteFiltro || undefined,
                        dataInicial: dataInicialFiltro || undefined,
                        dataFinal: dataFinalFiltro || undefined
                    }
                };

                //const response = await apiPetro.get<SumulaResumo[]>('analise/listarsumulas', config);
                const response = await apiPetro.get<{
                    itens: SumulaResumo[],
                    totalPaginas: number
                }>('analise/listarsumulas', config);
                if (response.data && Array.isArray(response.data.itens)) {
                    // Ordena as súmulas por data de criação em ordem decrescente (mais recente primeiro)
                    // const sumulasOrdenadas = response.data.sort((a, b) => {
                    //     const dataA = new Date(a.dataCriacao).getTime();
                    //     const dataB = new Date(b.dataCriacao).getTime();
                    //     return dataB - dataA; // Ordem decrescente
                    // });
                    setSumulas(response.data.itens);
                    setTotalPaginas(response.data.totalPaginas);
                } else {
                    setSumulas([]);
                    setTotalPaginas(1);
                }
            } catch (error) {
                console.error('Erro ao buscar súmulas:', error);
                setSumulas([]);
                setTotalPaginas(1);
            } finally {
                setLoading(false);
            }
        };

        fetchSumulas();
    }, [pagina, tamanho, codigoGrupoFiltro, statusFiltro, usuarioCriacaoFiltro, nomeGrupoFiltro, codigoClienteFiltro, dataInicialFiltro, dataFinalFiltro]);

    const handleRedirect = (id: string) => {
        navigate(`/sumula/${id}`);
    };

    const handleBuscar = () => {
        if (dataInicialBusca && dataFinalBusca && dataFinalBusca < dataInicialBusca) {
            setErroPeriodo('A data final não pode ser anterior à data inicial.');
            return;
        }
        setErroPeriodo('');
        setCodigoGrupoFiltro(codigoGrupoBusca);
        setStatusFiltro(statusBusca);
        setUsuarioCriacaoFiltro(usuarioCriacaoBusca);
        setNomeGrupoFiltro(nomeGrupoBusca);
        setCodigoClienteFiltro(codigoClienteBusca);
        setDataInicialFiltro(dataInicialBusca);
        setDataFinalFiltro(dataFinalBusca);
        setPagina(1);
    };

    return (
        <>
            <Header />
            <br />
            <Container>
                <Title>Lista de Súmulas</Title>
                {/* Filtros e Controles de Paginação */}
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', margin: '20px 0', gap: 8 }}>
                    <input
                        type="text"
                        placeholder="Filtrar por Código do Grupo"
                        value={codigoGrupoBusca}
                        onChange={e => setCodigoGrupoBusca(e.target.value)}
                        style={{ height: 28, padding: '0 8px' }}
                    />
                    <input
                        type="text"
                        placeholder="Nome do Grupo Econômico"
                        value={nomeGrupoBusca}
                        onChange={e => setNomeGrupoBusca(e.target.value)}
                        style={{ height: 28, padding: '0 8px' }}
                    />
                    <input
                        type="text"
                        placeholder="Código do Cliente"
                        value={codigoClienteBusca}
                        onChange={e => setCodigoClienteBusca(e.target.value)}
                        style={{ height: 28, padding: '0 8px' }}
                    />
                    <input
                        type="text"
                        placeholder="E-mail do usuário de inclusão"
                        value={usuarioCriacaoBusca}
                        onChange={e => setUsuarioCriacaoBusca(e.target.value)}
                        style={{ height: 28, padding: '0 8px', minWidth: 220 }}
                    />
                    {usuarioLogado?.perfil !== 'assessor' && usuarioLogado?.perfil !== 'Assessor' && (
                        <select
                            value={statusBusca}
                            onChange={e => setStatusBusca(e.target.value)}
                            style={{ height: 32, padding: '0 8px' }}
                        >
                            <option value="">Todos os Status</option>
                            <option value="0">Assessor</option>
                            <option value="1">Gestão Riscos</option>
                            <option value="3">Coordenação Riscos</option>
                            <option value="4">Diretoria</option>
                            <option value="5">Despachado</option>
                            <option value="6">Finalizado</option>
                        </select>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        Data Inicial:
                        <input
                            type="date"
                            value={dataInicialBusca}
                            onChange={e => setDataInicialBusca(e.target.value)}
                            style={{ height: 28, padding: '0 8px' }}
                        />
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        Data Final:
                        <input
                            type="date"
                            value={dataFinalBusca}
                            onChange={e => setDataFinalBusca(e.target.value)}
                            style={{ height: 28, padding: '0 8px' }}
                        />
                    </label>
                    <button
                        onClick={() => setPagina((p) => Math.max(1, p - 1))}
                        disabled={pagina === 1}
                    >
                        Anterior
                    </button>
                    <span style={{ margin: '0 10px' }}>
                        Página {pagina} de {totalPaginas}
                    </span>
                    <button
                        onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                        disabled={pagina === totalPaginas}
                    >
                        Próxima
                    </button>
                    <select
                        value={tamanho}
                        onChange={e => setTamanho(Number(e.target.value))}
                        style={{ height: 28 }}
                    >
                        {[5, 10, 20, 50].map(opt => (
                            <option key={opt} value={opt}>{opt} por página</option>
                        ))}
                    </select>
                    <button
                        onClick={handleBuscar}
                        disabled={loading}
                    >
                        Buscar
                    </button>
                </div>
                {erroPeriodo && (
                    <div style={{ textAlign: 'center', color: 'var(--error)', marginBottom: 12 }}>
                        {erroPeriodo}
                    </div>
                )}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        Carregando súmulas...
                    </div>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <Th>Código do Grupo</Th>
                                <Th>Código Cliente</Th>
                                <Th>Nome do Grupo</Th>
                                <Th>Data da Súmula</Th>
                                <Th>Status</Th>
                                <Th>Ações</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {sumulas && sumulas.length > 0 ? (
                                sumulas.map((s) => (
                                    <tr key={s.id}>
                                        <Td>{s.codigoGrupo}</Td>
                                        <Td>{s.codigoCliente ?? '—'}</Td>
                                        <Td>{s.nomeGrupo}</Td>
                                        <Td>{formatDateToBR(s.dataCriacao)}</Td>
                                        <Td>{getStatusText(s.statusSumula)}</Td>
                                        <Td>
                                            <button onClick={() => handleRedirect(s.id)}>
                                                Visualizar Súmula
                                            </button>
                                        </Td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <Td colSpan={6} style={{ textAlign: 'center' }}>
                                        Nenhuma súmula encontrada
                                    </Td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </Container>
        </>
    );
};